import React, { useContext, useEffect, useMemo } from 'react';
import ListingContext from '../../../../providers/Listing/Listing.context';
import ListingInformationSection from '../../components/ListingInformationSection/ListingInformationSection.component';
import ListingInformationHeader from '../../components/ListingInformationHeader';
import { ListingLayout } from '../../../../../../../Buyer/components/ListingLayout';
import { mappedInfo, SECTIONS } from './utils';
import { useTranslation } from 'react-i18next';
import LeadContext from '../../../../../../../Buyer/providers/Lead/Lead.context';
import { useLocation } from 'react-router';
import classNames from 'classnames';
import { Link, useNavigate } from 'react-router-dom';
import { checkHasListing, checkIsUrgent, getCreateListingRoute } from 'utils';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import HelmetWrapper from 'components/HelmetWrapper';
import { BUYING_PATH, LCW_STATEMENT_OF_INFORMATION_ROUTE } from 'data/global';

import './ListingInformationPage.styles.scss';

type ListingInformationPageProps = {
  className?: string;
};

const ListingInformationPage: React.FC<ListingInformationPageProps> = ({
  className,
}) => {
  const { t } = useTranslation();

  const { listing: listingBuyer, dealRoom } = useContext(LeadContext);
  const { listing: listingSeller, dealRoom: dealRoomSeller } =
    useContext(ListingContext);

  const navigate = useNavigate();
  const { pathname, search } = useLocation();

  const isBuying = pathname.includes(BUYING_PATH);
  const listing = isBuying ? listingBuyer : listingSeller;
  const { isSuperAdmin } = useContext(CurrentUserContext);
  const dealRoomName = isBuying
    ? dealRoom?.portfolioName
    : dealRoomSeller?.name;

  const controlledInformationRender = useMemo(() => {
    const isMissingControlledInfo = !listing?.controlledInformation;

    const isControlledInfoCompleted =
      !isMissingControlledInfo &&
      Object.values(listing.controlledInformation).every(Boolean);

    return (
      <div
        className={classNames('controlled-information-preview', {
          'missing-info': !isControlledInfoCompleted,
        })}
      >
        <p>{t('controlledInformation')}</p>
        {!isMissingControlledInfo && (
          <div>
            {Object.entries(listing.controlledInformation).map(
              ([key, value]) => (
                <div key={key}>
                  <p>{t(key)}</p>
                  <p>{value ? t(value.toString()) : '-'}</p>
                </div>
              ),
            )}
          </div>
        )}
        {!isControlledInfoCompleted && (
          <p className="missing-info-message">
            {t('youAreMissingContrInfo')}{' '}
            <Link
              to={
                getCreateListingRoute(
                  listing.dealRoomId,
                  listing._id,
                  listing.type,
                  true,
                ) + `/${LCW_STATEMENT_OF_INFORMATION_ROUTE}`
              }
            >
              {t('youAreMissingContrInfoLink')}
            </Link>
          </p>
        )}
      </div>
    );
  }, [listing, t]);

  useEffect(() => {
    if (isBuying && !checkHasListing(dealRoom?.listingStage)) {
      navigate('../profile', { replace: true });
    }
  }, [dealRoom, isBuying, navigate]);

  useEffect(() => {
    if (search) {
      navigate(pathname, { replace: true });
    }
  }, [navigate, pathname, search]);

  return (
    <ListingLayout hideSidePanel={!isBuying} isBuying={isBuying}>
      <div className={classNames('listing-information-page', className)}>
        <HelmetWrapper title={`Listing - ${dealRoomName}`} />

        <ListingInformationHeader
          text={t(isBuying ? 'theOpportunity' : 'listingInformation')}
        />

        {Object.entries(SECTIONS).map(([key, value]) => {
          if (!value) return null;

          if (
            key === 'statementOfInformation' &&
            (isBuying || checkIsUrgent(listing.type))
          ) {
            return null;
          }

          const content = mappedInfo(
            value,
            listing,
            isSuperAdmin,
            key === 'sellerInformation',
          );

          return (
            <React.Fragment key={key}>
              {content &&
                !!Object.entries(content).filter(([_, value]) => !!value)
                  .length && (
                  <ListingInformationSection
                    headerText={t(key)}
                    content={content}
                  />
                )}
              {!isBuying &&
                key === 'sellerInformation' &&
                listing?.controlledInformation &&
                controlledInformationRender}
            </React.Fragment>
          );
        })}
      </div>
    </ListingLayout>
  );
};

export default ListingInformationPage;
